/**
 * FrontDeskReaderTab.tsx
 *
 * Settings → Front Desk Reader: where a receptionist points this particular
 * computer at the desk it is sitting on, and where an administrator creates
 * the desks and gets the link that goes on the sticker.
 *
 * All of the tab's logic lives here rather than in `SettingsPage`, which stays
 * a shell of tab buttons.
 *
 * Two things about this file are load-bearing rather than cosmetic:
 *
 *   1. Every write to the per-computer config is followed by a
 *      `CONFIG_EVENT` dispatch. `useFrontDeskArrivals` (mounted globally from
 *      Layout) listens for it because the DOM `storage` event does not fire in
 *      the tab that performed the write — without the dispatch the toggle
 *      appears to work and nothing polls until the page is reloaded.
 *   2. The reader-pad card degrades to sticker guidance. The bridge's
 *      `/desk-tag` routes are hardware-gated (plan KTD8) and most sites will
 *      never have one, so "no pad answered" is the expected state and must not
 *      read as a failure.
 *
 * The audience is reception staff, so nothing here says token, link rotation,
 * bridge, poll or endpoint on screen — the mechanism is documented in comments
 * like this one and kept out of the interface. `FrontDeskReaderTab.test.tsx`
 * asserts that with a banned-word sweep over the rendered text.
 *
 * The small presentational helpers below are deliberate copies of the ones in
 * `SettingsPage.tsx`: they are private to that file, and exporting them would
 * mean editing a page this unit is meant to leave alone apart from its one new
 * tab entry.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Nfc, Plus, RefreshCw } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import {
  listDesks,
  createDesk,
  updateDesk,
  rotateDeskToken,
  type FrontDeskDesk,
} from '@/services/frontDesk.service';
import {
  getDeskTagStatus,
  setDeskTag,
  clearDeskTag,
} from '@/services/scannerBridge.service';
import { listPatientFacilities } from '@/services/patients.service';
import { saveConfig, clearConfig, loadConfig, CONFIG_EVENT } from '@/lib/frontDeskReader';

// ─── Presentational helpers (mirroring SettingsPage.tsx) ─────────────────────

function FieldLabel({ htmlFor, children }: { htmlFor?: string; children: React.ReactNode }) {
  return (
    <label htmlFor={htmlFor} className="block text-sm font-medium text-slate-700 mb-1">
      {children}
    </label>
  );
}

function SuccessBanner({ message }: { message: string }) {
  return (
    <div className="flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
      <Check className="h-4 w-4 flex-shrink-0" /> {message}
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="rounded-lg border bg-red-50 border-red-200 px-4 py-3 text-sm text-red-700">
      {message}
    </div>
  );
}

// ─── Time helpers ────────────────────────────────────────────────────────────

/**
 * Milliseconds for a UTC timestamp from the API, which may arrive either as
 * ISO-8601 or as MySQL's "YYYY-MM-DD HH:MM:SS". The second form has no zone
 * marker, and `Date.parse` reads a bare date-time as *local* — seven hours out
 * in Pacific — so the marker is added before parsing.
 */
function parseUtc(value: string): number {
  const hasZone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(value);
  const normalized = hasZone ? value : `${value.trim().replace(' ', 'T')}Z`;
  const parsed = Date.parse(normalized);
  return Number.isNaN(parsed) ? Date.now() : parsed;
}

function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? '' : 's'} ago`;
}

/** "just now" / "2 minutes ago" / "3 hours ago" / "4 days ago". */
function timeAgo(value: string): string {
  const seconds = Math.max(0, Math.round((Date.now() - parseUtc(value)) / 1000));
  if (seconds < 45) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return plural(minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (hours < 24) return plural(hours, 'hour');
  return plural(Math.round(hours / 24), 'day');
}

// ─── Copy for one desk in the picker ─────────────────────────────────────────

function deskOptionLabel(desk: FrontDeskDesk): string {
  const where = desk.facilityName.trim() || 'All locations';
  return `${desk.name} — ${where}${desk.active ? '' : ' (switched off)'}`;
}

// ─── The tab ─────────────────────────────────────────────────────────────────

export function FrontDeskReaderTab() {
  const { isAdmin, accountId, user } = useAuth();
  const account = accountId ?? 0;
  const staffUser = user?.id ?? 0;
  const queryClient = useQueryClient();

  const [enabled, setEnabled] = useState(false);
  const [selectedDeskID, setSelectedDeskID] = useState(0);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [newDeskName, setNewDeskName] = useState('');
  const [newDeskFacility, setNewDeskFacility] = useState('0');
  const [confirmingNewLink, setConfirmingNewLink] = useState(false);
  const [copied, setCopied] = useState(false);
  const linkRef = useRef<HTMLInputElement>(null);

  // ── Data ──────────────────────────────────────────────────────────────────

  const {
    data: desks,
    isLoading: desksLoading,
    isError: desksError,
  } = useQuery({ queryKey: ['front-desk-desks'], queryFn: listDesks });

  const { data: facilities } = useQuery({
    queryKey: ['patient-facilities'],
    queryFn: listPatientFacilities,
    enabled: isAdmin,
  });

  const { data: pad } = useQuery({
    queryKey: ['front-desk-pad'],
    queryFn: getDeskTagStatus,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const deskList = desks ?? [];
  const selectedDesk = deskList.find((d) => d.deskID === selectedDeskID) ?? null;

  // ── Per-computer config ───────────────────────────────────────────────────

  // Restore what this computer was already set up for. `loadConfig` returns
  // null for any "off" case, including a config saved under another account,
  // so there is nothing further to check here.
  useEffect(() => {
    const saved = loadConfig(account, staffUser);
    if (!saved) return;
    setEnabled(true);
    setSelectedDeskID(saved.deskID);
  }, [account, staffUser]);

  // Default the picker to something sensible once the desks arrive. This only
  // moves component state — it must never write the config, or opening
  // Settings would switch a computer on that nobody opted in.
  useEffect(() => {
    if (deskList.length === 0) return;
    setSelectedDeskID((current) => {
      if (current > 0 && deskList.some((d) => d.deskID === current)) return current;
      return (deskList.find((d) => d.active) ?? deskList[0]).deskID;
    });
    // Depends on the query result, not the `?? []` alias, which is a fresh
    // array on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desks]);

  /**
   * Write (or remove) this computer's desk and tell the polling hook.
   *
   * "Off" clears the stored value rather than storing `enabled: false`, so a
   * switched-off computer leaves nothing behind pointing at a desk.
   */
  const applyConfig = useCallback(
    (isOn: boolean, deskID: number, deskName: string) => {
      if (isOn && deskID > 0) {
        saveConfig({ enabled: true, accountID: account, userID: staffUser, deskID, deskName });
      } else {
        clearConfig();
      }
      window.dispatchEvent(new Event(CONFIG_EVENT));
    },
    [account],
  );

  function toggleEnabled(next: boolean) {
    if (next && selectedDeskID <= 0) {
      setMessage({ type: 'error', text: 'Choose which desk this computer is at first.' });
      return;
    }
    setMessage(null);
    setEnabled(next);
    applyConfig(next, selectedDeskID, selectedDesk?.name ?? '');
  }

  function chooseDesk(value: string) {
    const deskID = Number(value) || 0;
    setSelectedDeskID(deskID);
    setCopied(false);
    setConfirmingNewLink(false);
    const desk = deskList.find((d) => d.deskID === deskID);
    applyConfig(enabled, deskID, desk?.name ?? '');
  }

  // ── Desk administration ───────────────────────────────────────────────────

  const addMutation = useMutation({
    mutationFn: () =>
      createDesk({ name: newDeskName.trim(), facilityID: Number(newDeskFacility) || 0 }),
    onSuccess: (desk) => {
      setNewDeskName('');
      setSelectedDeskID(desk.deskID);
      setMessage({ type: 'success', text: 'Desk added. Its check-in link is below.' });
      queryClient.invalidateQueries({ queryKey: ['front-desk-desks'] });
    },
    onError: () =>
      setMessage({
        type: 'error',
        text: 'Could not add the desk. Give it a name and try again, or ask another administrator to add it.',
      }),
  });

  const activeMutation = useMutation({
    mutationFn: (nextActive: boolean) => updateDesk(selectedDeskID, { active: nextActive }),
    onSuccess: (desk) => {
      setMessage({
        type: 'success',
        text: desk.active ? 'This pad is switched on again.' : 'This pad is switched off.',
      });
      queryClient.invalidateQueries({ queryKey: ['front-desk-desks'] });
    },
    onError: () =>
      setMessage({
        type: 'error',
        text: 'Could not change the pad. Try again, or ask another administrator to change it.',
      }),
  });

  const newLinkMutation = useMutation({
    mutationFn: () => rotateDeskToken(selectedDeskID),
    onSuccess: () => {
      setConfirmingNewLink(false);
      setCopied(false);
      setMessage({
        type: 'success',
        text: 'New check-in link ready. Write it to a new sticker and throw the old sticker away.',
      });
      queryClient.invalidateQueries({ queryKey: ['front-desk-desks'] });
    },
    onError: () =>
      setMessage({
        type: 'error',
        text: 'Could not get a new link. Try again, or ask another administrator to get one.',
      }),
  });

  // ── Reader pad ────────────────────────────────────────────────────────────

  const sendToPadMutation = useMutation({
    mutationFn: (url: string) => setDeskTag(url),
    onSuccess: (status) => {
      queryClient.setQueryData(['front-desk-pad'], status);
      setMessage(
        status.available
          ? { type: 'success', text: 'The pad is showing this desk now.' }
          : {
              type: 'error',
              text: 'The pad stopped answering. Check it is plugged in, or use a sticker instead.',
            },
      );
    },
    onError: () =>
      setMessage({
        type: 'error',
        text: 'The pad stopped answering. Check it is plugged in, or use a sticker instead.',
      }),
  });

  const stopPadMutation = useMutation({
    mutationFn: () => clearDeskTag(),
    onSuccess: (status) => {
      queryClient.setQueryData(['front-desk-pad'], status);
      setMessage({ type: 'success', text: 'The pad has stopped showing a desk.' });
    },
    onError: () =>
      setMessage({
        type: 'error',
        text: 'The pad stopped answering. Check it is plugged in, or use a sticker instead.',
      }),
  });

  // ── Copying the link ──────────────────────────────────────────────────────

  async function copyLink() {
    const link = selectedDesk?.tapUrl ?? '';
    if (!link) return;
    // `navigator.clipboard` is absent on plain HTTP and in older browsers, and
    // can reject when the page is not focused; both fall back to selecting the
    // field so the operator can copy it by hand.
    const clipboard = navigator.clipboard;
    if (clipboard && typeof clipboard.writeText === 'function') {
      try {
        await clipboard.writeText(link);
        setCopied(true);
        return;
      } catch {
        // Fall through to the manual path below.
      }
    }
    const field = linkRef.current;
    if (field) {
      field.focus();
      field.select();
    }
    setMessage({ type: 'success', text: 'The link is highlighted. Press Ctrl+C to copy it.' });
  }

  // ── Render ────────────────────────────────────────────────────────────────

  const lastTap = selectedDesk?.lastArrivalAtUtc
    ? `Last tap: ${timeAgo(selectedDesk.lastArrivalAtUtc)}`
    : 'No taps yet.';

  const facilityOptions = [
    { value: '0', label: 'All locations' },
    ...(facilities ?? [])
      .filter((f) => f.active)
      .map((f) => ({ value: String(f.facilityId), label: f.facilityName })),
  ];

  return (
    <div className="space-y-6 max-w-2xl">
      <h2 className="text-lg font-semibold text-slate-800">Front Desk Reader</h2>

      {message &&
        (message.type === 'success' ? (
          <SuccessBanner message={message.text} />
        ) : (
          <ErrorBanner message={message.text} />
        ))}

      {/* ── The switch ───────────────────────────────────────────────────── */}
      <label className="flex items-start gap-3">
        <input
          id="frontDeskEnabled"
          type="checkbox"
          role="switch"
          checked={enabled}
          onChange={(e) => toggleEnabled(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
        />
        <span>
          <span className="block text-sm font-medium text-slate-800">
            Use this computer as a front desk reader
          </span>
          <span className="block text-xs text-slate-500">
            When a patient taps the pad with their phone, this computer opens their chart.
          </span>
        </span>
      </label>

      {/* ── Which desk ───────────────────────────────────────────────────── */}
      {desksLoading ? (
        <p className="text-sm text-slate-500">Loading...</p>
      ) : desksError ? (
        <ErrorBanner message="Could not load the desks. Refresh the page to try again." />
      ) : deskList.length === 0 ? (
        !isAdmin && (
          <p className="text-sm text-slate-600">Ask an administrator to add a front desk pad.</p>
        )
      ) : (
        <div>
          <FieldLabel htmlFor="frontDeskDesk">Which desk is this computer at?</FieldLabel>
          <select
            id="frontDeskDesk"
            value={String(selectedDeskID)}
            onChange={(e) => chooseDesk(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            {deskList.map((desk) => (
              <option key={desk.deskID} value={String(desk.deskID)}>
                {deskOptionLabel(desk)}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-slate-500">{lastTap}</p>
        </div>
      )}

      {/* ── The link for the sticker ─────────────────────────────────────── */}
      {selectedDesk && (
        <div>
          <FieldLabel htmlFor="frontDeskLink">Check-in link for this desk</FieldLabel>
          <div className="flex items-center gap-2">
            <input
              id="frontDeskLink"
              ref={linkRef}
              type="text"
              value={selectedDesk.tapUrl}
              readOnly
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500"
            />
            <button
              type="button"
              onClick={() => void copyLink()}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              <Copy className="h-4 w-4" />
              Copy
            </button>
          </div>
          {copied && <p className="mt-1 text-xs text-green-700">Copied.</p>}
          <p className="mt-1 text-xs text-slate-500">
            Write this link to an NFC sticker with a free phone app such as NFC Tools, then place
            the sticker under a "Tap to check in" sign.
          </p>

          {isAdmin && (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => activeMutation.mutate(!selectedDesk.active)}
                disabled={activeMutation.isPending}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                {selectedDesk.active ? 'Turn this pad off' : 'Turn it on'}
              </button>
              {!confirmingNewLink && (
                <button
                  type="button"
                  onClick={() => setConfirmingNewLink(true)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  <RefreshCw className="h-4 w-4" />
                  Get a new tag link
                </button>
              )}
            </div>
          )}

          {isAdmin && confirmingNewLink && (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm text-amber-800">
                Every sticker already written for this desk will stop working. You will need to
                write the new link to a new sticker before patients can tap again.
              </p>
              <div className="mt-3 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => newLinkMutation.mutate()}
                  disabled={newLinkMutation.isPending}
                  className="rounded-lg bg-amber-600 px-3 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
                >
                  Yes, get a new link
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingNewLink(false)}
                  className="text-sm font-medium text-slate-600 hover:text-slate-800"
                >
                  Keep the current link
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── The optional reader pad ──────────────────────────────────────── */}
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <Nfc className="h-4 w-4" />
          Reader pad
        </h3>
        {!pad || !pad.available ? (
          <p className="mt-2 text-sm text-slate-600">
            No reader pad found on this computer. A sticker works just as well.
          </p>
        ) : (
          <>
            <p className="mt-2 text-sm text-slate-600">
              {pad.presenting && selectedDesk && pad.url === selectedDesk.tapUrl
                ? 'The pad is showing this desk.'
                : pad.presenting
                  ? 'The pad is showing a different desk.'
                  : 'The pad is not showing a desk yet.'}
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              {selectedDesk && (
                <button
                  type="button"
                  onClick={() => sendToPadMutation.mutate(selectedDesk.tapUrl)}
                  disabled={sendToPadMutation.isPending}
                  className="rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
                >
                  Send to reader pad
                </button>
              )}
              {pad.presenting && (
                <button
                  type="button"
                  onClick={() => stopPadMutation.mutate()}
                  disabled={stopPadMutation.isPending}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  Stop presenting
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* ── Adding a desk ────────────────────────────────────────────────── */}
      {isAdmin && (
        <div className="rounded-lg border border-slate-200 p-4">
          <h3 className="mb-3 text-sm font-semibold text-slate-800">Add a desk</h3>
          <div className="space-y-3">
            <div>
              <FieldLabel htmlFor="frontDeskNewName">Desk name</FieldLabel>
              <input
                id="frontDeskNewName"
                type="text"
                value={newDeskName}
                onChange={(e) => setNewDeskName(e.target.value)}
                placeholder="Front counter"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <FieldLabel htmlFor="frontDeskNewFacility">Location</FieldLabel>
              <select
                id="frontDeskNewFacility"
                value={newDeskFacility}
                onChange={(e) => setNewDeskFacility(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                {facilityOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={() => addMutation.mutate()}
              disabled={addMutation.isPending || newDeskName.trim() === ''}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
            >
              <Plus className="h-4 w-4" />
              Add desk
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
